import { Activity } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { useActivity, useStatusHistory } from '@/hooks/use-loans';
import { formatCurrency } from '@/lib/format';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
}

const ACTION_LABELS: Record<string, string> = {
  'status.changed': 'Changed status',
  'comment.added': 'Added a comment',
  'assignment.created': 'Assigned a team member',
  'assignment.removed': 'Removed a team member',
  'schedule.generated': 'Generated repayment schedule',
  'payment.received': 'Payment received',
  'loan.created': 'Created the loan',
  'loan.updated': 'Updated loan details',
  'funding.allocated': 'Allocated funding',
  'funding.deallocated': 'Removed funding allocation',
  'approval.workflow_started': 'Started approval workflow',
  'approval.step_submitted': 'Submitted approval decision',
  'condition.created': 'Added a condition',
  'condition.satisfied': 'Satisfied a condition',
  'condition.waived': 'Waived a condition',
  'invoice.created': 'Created an invoice',
  'invoice.issued': 'Issued an invoice',
  'invoice.payment_recorded': 'Recorded an invoice payment',
  'invoice.cancelled': 'Cancelled an invoice',
  'fee.schedule_created': 'Created a fee schedule',
  'fee.schedule_updated': 'Updated a fee schedule',
  'fee.schedule_waived': 'Waived a fee schedule',
  'fee.charged': 'Charged a fee',
  'fee.charge_waived': 'Waived a fee charge',
  'fee.charge_deferred': 'Deferred a fee charge',
  'fee.charge_paid': 'Recorded a fee payment',
  'document.uploaded': 'uploaded a document',
  'document.deleted': 'deleted a document',
  'settlement.created': 'created a settlement statement',
  'settlement.updated': 'updated a settlement statement',
  'settlement.reconciled': 'reconciled a settlement statement',
  'communication.created': 'recorded a communication',
  'communication.updated': 'updated a communication',
  'communication.replied': 'replied to a communication',
  'communication.resolved': 'resolved a communication',
  'email.sent': 'sent an email',
};

const FIELD_LABELS: Record<string, string> = {
  interestRate: 'Interest rate',
  defaultRate: 'Default rate',
  settlementDate: 'Settlement date',
  maturityDate: 'Maturity date',
  paymentFrequency: 'Payment frequency',
  termMonths: 'Term',
  principal: 'Loan amount',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function actionLabel(actionType: string): string {
  const label = ACTION_LABELS[actionType] ?? actionType.replace(/[._]/g, ' ');
  return `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

function activityDetails(actionType: string, details: Record<string, unknown> | null | undefined) {
  if (!details) return null;

  if (actionType === 'funding.allocated') {
    const name = details.fundingLineName;
    const amount = Number(details.allocatedAmount);
    return [
      name ? `Funding line: ${name}` : null,
      Number.isFinite(amount) ? `Amount: ${formatCurrency(amount)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (actionType === 'schedule.generated' && typeof details.instalmentCount === 'number') {
    return `${details.instalmentCount} instalments generated`;
  }
  if (actionType === 'loan.updated' && Array.isArray(details.fields)) {
    return `Updated: ${details.fields.map(field => fieldLabel(String(field))).join(', ')}`;
  }
  if (actionType === 'loan.created' && details.loanNumber) {
    return `Loan ${String(details.loanNumber)}`;
  }
  if (actionType.startsWith('document.') && details.name) {
    return `Document: ${String(details.name)}`;
  }
  if (actionType.startsWith('settlement.') && details.statementNumber) {
    return `Statement ${String(details.statementNumber)}`;
  }
  if (actionType.startsWith('communication.') && details.subject) {
    return String(details.subject);
  }
  if (actionType === 'email.sent' && details.subject) {
    return String(details.subject);
  }
  if (actionType === 'payment.received' && details.amount !== undefined) {
    return `Amount: ${formatCurrency(Number(details.amount))}`;
  }
  if (actionType.startsWith('invoice.') && details.invoiceNumber) {
    return `Invoice ${String(details.invoiceNumber)}`;
  }
  if (actionType.startsWith('fee.') && details.totalAmount !== undefined) {
    return `Amount: ${formatCurrency(Number(details.totalAmount))}`;
  }
  if (details.reason) return String(details.reason);
  return null;
}

function actionIcon(actionType: string): string {
  if (actionType.startsWith('status')) return 'bg-blue-100 text-blue-600';
  if (actionType.startsWith('comment')) return 'bg-green-100 text-green-600';
  if (actionType.startsWith('assignment')) return 'bg-purple-100 text-purple-600';
  if (actionType.startsWith('payment')) return 'bg-emerald-100 text-emerald-600';
  return 'bg-gray-100 text-gray-600';
}

interface Props {
  loanId: string;
}

export function ActivityTab({ loanId }: Props) {
  const { data: activity, isLoading: loadingActivity } = useActivity(loanId);
  const { data: statusHistory, isLoading: loadingHistory } = useStatusHistory(loanId);

  const isLoading = loadingActivity || loadingHistory;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading activity…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status history */}
      {statusHistory && statusHistory.length > 0 && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Status History</h3>
          <div className="space-y-3">
            {statusHistory.map(t => (
              <div key={t.id} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{t.fromStatus}</span>
                    <span className="text-gray-400 mx-1.5">&rarr;</span>
                    <span className="font-medium">{t.toStatus}</span>
                  </p>
                  {t.reason && <p className="text-xs text-gray-500 mt-0.5">{t.reason}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t.user ? `${t.user.firstName} ${t.user.lastName}` : 'System'}
                    {' · '}
                    {timeAgo(t.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Activity log */}
      <section className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Activity Log</h3>
        {!activity || activity.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Activity className="w-8 h-8 mb-2" />
            <p className="text-sm">No activity yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activity.map(entry => {
              const details = activityDetails(entry.actionType, entry.details);
              return (
                <div key={entry.id} className="flex items-start gap-3">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${actionIcon(entry.actionType)}`}
                  >
                    <span className="text-[9px] font-bold">
                      {entry.actor ? `${entry.actor.firstName[0]}${entry.actor.lastName[0]}` : 'SY'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">
                        {entry.actor
                          ? `${entry.actor.firstName} ${entry.actor.lastName}`
                          : 'System'}
                      </span>{' '}
                      <span className="text-gray-500">{actionLabel(entry.actionType)}</span>
                    </p>
                    {details && <p className="text-xs text-gray-500 mt-0.5">{details}</p>}
                    <p className="text-xs text-gray-400 mt-0.5">{timeAgo(entry.createdAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
